import { useRouter } from 'next/navigation';
import { Droplet, Help, Meter, Notification, Work } from '@public';
import { capitalize } from 'lodash';

import styles from './emptyList.module.scss';

const itemTypes = {
	Device: 'Device',
	Authorisation: 'Authorisation',
	Work: 'Work',
	Notification: 'Notification',
	WaterAccount: 'Water Account',
	Linking: 'Linking',
	Announcement: 'Announcement',
};

type itemTypes = (typeof itemTypes)[keyof typeof itemTypes];
/**
 * Props for Empty list
 */
export interface EmptyListProps {
	/** Type of items :  `Device` | `Authorisation` | `Work` | `Notification` */
	itemType: itemTypes;
	/** Overwrite the name of item type */
	itemOverwrittenName?: string;
	/** Display message by legal entity or not */
	isItemByLegalEntity?: boolean;
	/** Callback function to report an issue */
	onReportClick?: () => void;
	/** Show link to contact page */
	showContactLink?: boolean;
}

/**
 * Generate content bloc saying there is no items of the given type or the page is loading.
 * @param EmptyList
 * @returns
 */
export const EmptyList = ({
	itemType,
	itemOverwrittenName,
	isItemByLegalEntity = false,
	onReportClick,
	showContactLink,
}: EmptyListProps) => {
	const router = useRouter();
	const itemConcept = itemOverwrittenName ? itemOverwrittenName : itemType;
	const itemName = capitalize(itemConcept.trim());
	const itemsName = itemConcept.toLowerCase() + 's';
	let icon;

	switch (itemType) {
		case itemTypes.Device:
			icon = <Meter customStyles={styles['title-icon']} />;
			break;
		case itemTypes.Authorisation:
			icon = <Droplet customStyles={styles['title-icon']} />;
			break;
		case itemTypes.Work:
			icon = <Work customStyles={styles['title-icon']} />;
			break;
		case itemTypes.Notification:
			icon = <Notification customStyles={styles['title-icon']} />;
			break;
		case itemTypes.WaterAccount:
			icon = <Droplet customStyles={styles['title-icon']} />;
			break;
		case itemTypes.Linking:
			icon = <Help customStyles={styles['title-icon']} />;
			break;
		case itemTypes.Announcement:
			icon = <Help customStyles={styles['title-icon']} />;
			break;
	}

	const text =
		`You have no ${itemsName} associated with ` +
		(isItemByLegalEntity ? 'this legal entity.' : 'your account.');

	const navigateToContact = () => {
		router.push('/contact');
	};

	return (
		<div className={styles.wrapper} data-testid="emptyList">
			<>
				{icon}
				<div className={styles['title-icon-background']} />
			</>
			<h2 className={styles.title}>No {itemsName}</h2>
			<p className={styles.text}>{text}</p>
			{onReportClick && (
				<p className={styles.text}>
					{itemName} missing? <a onClick={onReportClick}>Report</a> this to us.
				</p>
			)}
			{showContactLink && (
				<p className={styles.text}>
					{onReportClick ? 'Or ' : itemName + ' missing? '}{' '}
					<a onClick={navigateToContact} data-testid="contactUs">
						Contact us
					</a>
				</p>
			)}
		</div>
	);
};
